<script>
  import { onMount } from "svelte";
  import { slide } from "svelte/transition";
  import { formatDate, formatPrice } from "../../services/utilities.js";
  import { getUserOrders } from "../../models/orders.js";
  import CartList from "../../components/CartList.svelte";
  import OrderTotals from "../../components/OrderTotals.svelte";
  import Alert from "../../components/Alert.svelte";
  import Pagination from "../../components/Pagination.svelte";

  let active = null;
  let ordersList = [];
  let orderTotal = 0;
  let loading = true;
  let error = "";
  let page = 0;
  let limit = 20;
  $: maxPages = Math.ceil(orderTotal / limit);

  async function getOrders(p) {
    page += p;
    let orders = await getUserOrders(page, limit);
    if (!orders || !orders.orders) {
      error = "Unable to load orders. Please try again.";
      return;
    }
    orderTotal = orders.total;
    ordersList = orders.orders;
    ordersList.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }

  onMount(async () => {
    try {
      await getOrders(0);
    } catch (e) {
      error = "Unable to load orders. Please try again.";
    } finally {
      loading = false;
    }
  });
</script>

<style type="text/less">
  h2 {
    margin-bottom: var(--spacing);
  }

  .order-list {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
  }

  .order {
    border: 1px solid var(--col-gray-80);
    border-radius: 8px;
    overflow: hidden;
    background: var(--col-gray-100);
  }

  .overview {
    display: flex;
    align-items: center;
    padding: var(--spacing-sm);
    cursor: pointer;
    gap: var(--spacing-sm);
    user-select: none;

    &:hover {
      background: var(--col-gray-98);
    }
  }

  .arrow {
    color: var(--col-gray-60);
    width: 1.2rem;
    flex-shrink: 0;
    display: flex;
    align-items: center;

    .icon {
      width: 1.2rem;
      height: 1.2rem;
    }
  }

  .info {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs);
  }

  .info-id {
    font-weight: 700;
    font-size: 1rem;
  }

  .info-meta {
    display: flex;
    flex-wrap: wrap;
    gap: var(--spacing-xs);
    font-size: var(--font-size-sm);
    color: var(--col-gray-50);
  }

  .info-description {
    font-size: var(--font-size-sm);
    color: var(--col-gray-40);
  }

  .info-event {
    font-size: var(--font-size-sm);
    color: var(--col-gray-50);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .info-event a {
    color: var(--col-accent-2);
  }

  .info-return {
    font-size: var(--font-size-sm);
    color: var(--col-gray-50);
  }

  .info-price {
    font-weight: 600;
    color: var(--col-text);
  }

  .status {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
    flex-shrink: 0;
    font-size: var(--font-size-sm);
    font-weight: 600;
  }

  .details {
    border-top: 1px solid var(--col-gray-90);
    background: var(--col-gray-98);
  }

  .details-inner {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: var(--spacing);
    padding: var(--spacing);
    align-items: start;
  }

  .details-section-label {
    font-size: var(--font-size-xs);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--col-gray-50);
    font-weight: 600;
    margin-bottom: var(--spacing-sm);
  }

  // Hide the hardcoded "Order Totals" h2 from the shared component
  .details-totals :global(h2) {
    display: none;
  }

  // Tighten up the cart section margin inside the detail panel
  .details-items :global(.cart) {
    margin: 0;
  }

  // Remove top border from first cart item since we have our own section label
  .details-items :global(.cart-item:first-child) {
    border-top: none;
  }

  @media (max-width: 700px) {
    .details-inner {
      grid-template-columns: 1fr;
      gap: var(--spacing-sm);
      padding: var(--spacing-sm);
    }
  }

  .empty-state {
    text-align: center;
    padding: var(--spacing-lg);
    color: var(--col-gray-50);

    p {
      margin-bottom: var(--spacing-sm);
    }
  }

  @media (max-width: 600px) {
    .overview {
      align-items: flex-start;
    }

    .status {
      display: none;
    }

    .info-meta {
      flex-direction: column;
      gap: 2px;
    }
  }
</style>

<h2>Orders</h2>

{#if error}
  <Alert type="danger" icon="warning">{error}</Alert>
{/if}

{#if loading}
  <p>Loading...</p>
{:else if ordersList.length === 0}
  <div class="empty-state">
    <p>You haven't placed any orders yet.</p>
    <a href="/products" class="btn">Shop Now</a>
  </div>
{:else}
  <div class="order-list">
    {#each ordersList as order, i}
      <div class="order" class:active={active === i}>
        <div
          class="overview"
          on:click={() => { active === i ? (active = null) : (active = i); }}
        >
          <div class="arrow">
            {#if !order.auto_charge}
              <svg class="icon">
                <use href="/icons.svg#keyboard_arrow_{active === i ? 'down' : 'right'}" />
              </svg>
            {/if}
          </div>

          <div class="info">
            <span class="info-id">Order #{order.id}</span>
            {#if order.description}
              <span class="info-description">{order.description}</span>
            {/if}
            {#each order.events ?? [] as event}
              <span class="info-event">
                Event: <a href="/event/{event.id}" target="_blank">
                  {formatDate(event.start_ts * 1000, { showTime: false })} {event.title}
                </a>
              </span>
            {/each}
            <div class="info-meta">
              <span class="info-price">{formatPrice(order.total)}</span>
              <span>·</span>
              <span>{formatDate(order.created_at, { showTime: false })}</span>
            </div>
            {#if order.rental_return_time === "3999-01-01T00:00:00Z"}
              <span class="info-return">Subscription Rentals</span>
            {:else if order.rental_return_time !== "0001-01-01T00:00:00Z" && order.rental_return_time !== null}
              <span class="info-return">Return by {formatDate(order.rental_return_time, { showTime: false })}</span>
            {/if}
          </div>

          <div class="status">
            {#if order.auto_charge}
              <span>Autocharge</span>
              <span class="status-icon autocharge" />
            {:else}
              <span>{order.status}</span>
              <span class="status-icon {order.status.toLowerCase()}" />
            {/if}
          </div>
        </div>

        {#if active === i && !order.auto_charge}
          <div class="details" transition:slide>
            <div class="details-inner">
              <div class="details-items">
                <div class="details-section-label">Items</div>
                <CartList editable={false} items={order.order_products} orderDate={order.created_at} />
              </div>
              <div class="details-totals">
                <div class="details-section-label">Summary</div>
                <OrderTotals
                  subtotal={order.subtotal}
                  shipping={order.shipping_cost}
                  giftCard={{ id: order.gift_card_id, amount: order.gift_card_amount, valid: true }}
                  applyAccountBalanceAmount={order.apply_balance_amount}
                  discounts={order.discounts}
                  taxes={order.taxes}
                  fees={order.fees || []}
                  total={order.total}
                />
              </div>
            </div>
          </div>
        {/if}
      </div>
    {/each}

    <Pagination
      total={orderTotal}
      {maxPages}
      currentPage={page}
      prevPager={() => getOrders(-1)}
      pager={() => getOrders(1)}
    />
  </div>
{/if}
